import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
  createTicket,
  getTickets,
  getTicketMessages,
  ticketReply,
  updateTicketStatus,
  searchTicket,
} from '../../../utils/services/user.service';

const Support = () => {
  const [form, setForm] = useState({ subject: '', message: '' });
  const [files, setFiles] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [message, setMessage] = useState('');
  const [chatFiles, setChatFiles] = useState([]);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showAttentionModal, setShowAttentionModal] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalTickets, setTotalTickets] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const user = JSON.parse(localStorage.getItem('user'))?.data?.user;

  // Fetch tickets on component mount and when filters change
  useEffect(() => {
    fetchTickets();
  }, [pageNumber, statusFilter, priorityFilter, dateFilter]);

  const fetchTickets = async () => {
    try {
      setIsLoading(true);
      const payload = {
        status: statusFilter,
        limit: pageSize,
        page_number: pageNumber,
        user_id: user?.user_id,
      };
      const response = await getTickets(payload);
      if (response.data?.tickets) {
        setTickets(response.data.tickets);
        setTotalTickets(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch messages when a ticket is selected
  useEffect(() => {
    if (selectedTicket && selectedTicket._id && selectedTicket._id !== 'NEW' && selectedTicket.id !== 'NEW') {
      fetchTicketMessages(selectedTicket._id);
    }
  }, [selectedTicket]);

  const fetchTicketMessages = async (ticketId) => {
    try {
      setIsLoading(true);
      const requestData = {
        ticket_id: ticketId,
      };
      const response = await getTicketMessages(requestData);
      if (response.data?.tickets) {
        setTicketMessages(response.data.tickets);
      }
    } catch (error) {
      console.error('Error fetching ticket messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!form.subject || !form.message) {
      toast.error('Subject and message are required');
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('subject', form.subject);
      formData.append('message', form.message);
      if (files.length > 0) {
        files.forEach((file) => {
          formData.append('File', file);
        });
      }

      const response = await createTicket(formData);
      if (response.data?.status === 200) {
        toast.success('Ticket Created Successfully');
        setForm({ subject: '', message: '' });
        setFiles([]);
        setTicketMessages([]);
        fetchTickets();
        // Refresh to show the newly created ticket
        setTimeout(() => {
          fetchTickets();
        }, 500);
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Failed to create ticket');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message && chatFiles.length === 0) {
      toast.error('Either message or file must have data');
      return;
    }

    if (!selectedTicket || !selectedTicket._id) {
      toast.error('Please select a ticket');
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();   
      formData.append('ticket_id', selectedTicket._id);
      formData.append('message', message);
      chatFiles.forEach((file) => {
        formData.append('File', file);
      });

      const response = await ticketReply(formData);
      if (response.data?.success === true) {
        toast.success('Message sent successfully');
        setMessage('');
        setChatFiles([]);
        fetchTicketMessages(selectedTicket._id);
          } else {
        toast.error('Failed to send message');
          }
        } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket || !selectedTicket._id) {
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        ticket_id: selectedTicket._id,
        status: 'close',
      };
      const response = await updateTicketStatus(payload);
      if (response) {
        toast.success('Ticket Status Updated Successfully');
        setShowCloseModal(false);
        fetchTickets();
        if (selectedTicket) {
          setSelectedTicket({ ...selectedTicket, status: 'close' });
        }
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      toast.error('Failed to update ticket status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      fetchTickets();
      return;
    }

    try {
      const payload = {
        subject: query,
      };
      const response = await searchTicket(payload);
      if (response.data?.tickets) {
        setTickets(response.data.tickets);
        setTotalTickets(response.data.tickets.length);
      }
    } catch (error) {
      console.error('Error searching tickets:', error);
      toast.error('Failed to search tickets');
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      open: '#00f7d2',
      'in review': '#facc15',
      close: '#ef4444',
      closed: '#ef4444',
    };
    return statusMap[status?.toLowerCase()] || '#00f7d2';
  };

  const getPriorityColor = (priority) => {
    const priorityMap = {
      high: '#ef4444',
      medium: '#f59e0b',
      low: '#22c55e',
    };
    return priorityMap[priority?.toLowerCase()] || '#f59e0b';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('YYYY-MM-DD');
  };

  const filterStyle = {
    width: '100%',
    padding: '10px',
    marginTop: '10px',
    background: '#0d1220',
    border: '1px solid #1f2937',
    borderRadius: '10px',
    color: '#fff',
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0b0e14', color: '#e5e7eb' }}>
      {/* Loading Overlay */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <div style={{ color: '#00f7d2', fontSize: '18px' }}>Loading...</div>
        </div>
      )}

      {/* LEFT SIDEBAR */}
      <div
        style={{
          width: '340px',
          padding: '30px 20px',
          background: '#111827',
          borderRight: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <h2
          style={{
            background: 'linear-gradient(90deg,#00f7d2,#5ce9ff)',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Ticket Center
        </h2>

        {/* NEW TICKET BUTTON */}
        <button
          onClick={() => {
            setSelectedTicket({
              id: 'NEW',
              _id: null,
              subject: '',
              status: 'Draft',
              priority: 'Medium',
              date: new Date().toISOString().slice(0, 10),
              newMsg: false,
              timeline: [],
              chat: [],
            });
            setForm({ subject: '', message: '' });
            setFiles([]);
            setTicketMessages([]);
          }}
          style={{
            width: '100%',
            padding: '12px 16px',
            marginTop: '15px',
            marginBottom: '25px',
            borderRadius: '12px',
            background: 'linear-gradient(90deg,#00f7d2,#5ce9ff)',
            color: '#000',
            fontWeight: 600,
            fontSize: '16px',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          + New Ticket
        </button>

        {/* SEARCH */}
        <div style={{ marginBottom: '20px' }}>
                    <input
                      type="text"
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            style={filterStyle}
          />
        </div>

        {/* FILTERS */}
        <div style={{ marginTop: '20px', marginBottom: '30px' }}>
          <h4 style={{ color: '#5ce9ff' }}>Filters</h4>
          <select
            style={filterStyle}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPageNumber(1);
            }}
          >
            <option value="all">Status: All</option>
            <option value="open">Open</option>
            <option value="in review">In Review</option>
            <option value="close">Closed</option>
          </select>
          <select
            style={filterStyle}
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setPageNumber(1);
            }}
          >
            <option value="all">Priority: All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
                    <input
            type="date"
            style={filterStyle}
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setPageNumber(1);
            }}
          />
        </div>

        {/* TICKET LIST */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
          {tickets.map((ticket) => (
            <div
              key={ticket._id}
              onClick={() => setSelectedTicket(ticket)}
              style={{
                padding: '16px',
                background: selectedTicket?._id === ticket._id ? '#0d1220' : '#1a2234',
                borderRadius: '14px',
                border: '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer',
                position: 'relative',
                transition: '0.2s',
              }}
            >
              {ticket.user_read_status === false && (
                <span
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '10px',
                    height: '10px',
                    background: '#facc15',
                    borderRadius: '50%',
                  }}
                />
              )}
              <h4 style={{ margin: 0, color: '#00f7d2' }}>
                {ticket.subject}
              </h4>

              {/* PRIORITY BADGE */}
              {ticket.priority && (
                <span
                  style={{
                    padding: '3px 6px',
                    fontSize: '11px',
                    borderRadius: '8px',
                    background: getPriorityColor(ticket.priority),
                    color: '#000',
                    marginTop: '6px',
                    display: 'inline-block',
                  }}
                >
                  {ticket.priority}
                </span>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                <span style={{ opacity: 0.8, fontSize: '13px' }}>
                  {formatDate(ticket.createdAt)}
                </span>
                <span
                  style={{
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    background: getStatusColor(ticket.status),
                    color: '#000',
                  }}
                >
                  {ticket.status}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* PAGINATION */}
        {totalTickets > pageSize && (
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
              disabled={pageNumber === 1}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                background: pageNumber === 1 ? '#1a2234' : '#00f7d2',
                color: pageNumber === 1 ? '#666' : '#000',
                border: 'none',
                cursor: pageNumber === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Previous
            </button>
            <span style={{ padding: '8px', alignSelf: 'center' }}>
              Page {pageNumber} of {Math.ceil(totalTickets / pageSize)}
            </span>
            <button
              onClick={() => setPageNumber(pageNumber + 1)}
              disabled={pageNumber >= Math.ceil(totalTickets / pageSize)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                background: pageNumber >= Math.ceil(totalTickets / pageSize) ? '#1a2234' : '#00f7d2',
                color: pageNumber >= Math.ceil(totalTickets / pageSize) ? '#666' : '#000',
                border: 'none',
                cursor: pageNumber >= Math.ceil(totalTickets / pageSize) ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* RIGHT MAIN PANEL */}
      <div style={{ flex: 1, padding: '40px', overflowY: 'auto' }}>
        {!selectedTicket && (
          <h2 style={{ color: '#5ce9ff' }}>Select a ticket from the left</h2>
        )}

        {selectedTicket && selectedTicket.id === 'NEW' && (
          <div>
            <h2 style={{ color: '#5ce9ff', marginBottom: '10px' }}>
              Ticket #{selectedTicket.id} — {form.subject || 'New Ticket'}
            </h2>

            {/* STATUS BADGE */}
            <div
              style={{
                padding: '6px 12px',
                background: '#6b7280',
                color: '#000',
                width: 'fit-content',
                borderRadius: '10px',
                marginBottom: '20px',
              }}
            >
              {selectedTicket.status}
            </div>

            {/* CLOSE TICKET BUTTON */}
            <button
              onClick={() => setShowCloseModal(true)}
              style={{
                padding: '8px 14px',
                background: '#ef4444',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                marginBottom: '20px',
                cursor: 'pointer',
              }}
            >
              Close Ticket
            </button>

            {/* SUBJECT INPUT */}
            <div style={{ marginBottom: '20px' }}>
              <input
                type="text"
                placeholder="Subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#111827',
                  border: '1px solid #1f2937',
                  borderRadius: '10px',
                  color: '#fff',
                }}
              />
            </div>

            {/* TIMELINE */}
            <h3 style={{ color: '#00f7d2' }}>Timeline</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {selectedTicket.timeline.length === 0 ? (
                <li style={{ marginBottom: '6px', opacity: 0.6 }}>No timeline events yet</li>
              ) : (
                selectedTicket.timeline.map((step, i) => (
                  <li key={i} style={{ marginBottom: '6px', opacity: 0.9 }}>
                    {step}
                  </li>
                ))
              )}
            </ul>

            {/* CHAT */}
            <h3 style={{ color: '#00f7d2', marginTop: '30px' }}>Chat</h3>
            <div
              style={{
                background: '#0d1220',
                padding: '16px',
                borderRadius: '12px',
                height: '260px',
                overflowY: 'auto',
                border: '1px solid rgba(255,255,255,0.04)',
                marginBottom: '20px',
              }}
            >
              {selectedTicket.chat.length === 0 ? (
                <p style={{ opacity: 0.6, textAlign: 'center', marginTop: '100px' }}>
                  No messages yet
                </p>
              ) : (
                selectedTicket.chat.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      marginBottom: '12px',
                      textAlign: c.from === 'support' ? 'left' : 'right',
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-block',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        background: c.from === 'support' ? '#1e293b' : '#00f7d2',
                        color: c.from === 'support' ? '#fff' : '#000',
                      }}
                    >
                      {c.text}
                    </div>
                  </div>
                ))
              )}

              {/* FILE PREVIEW */}
              {files.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <h4 style={{ color: '#5ce9ff' }}>Files:</h4>
                  {files.map((f, i) => (
                    <div key={i} style={{ fontSize: '12px', opacity: 0.8 }}>
                      • {f.name}
                    </div>
                  ))}
                </div>                
              )}
              </div>

            {/* MESSAGE INPUT + FILE UPLOAD */}
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Write a message..."
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#111827',
                  border: '1px solid #1f2937',
                  borderRadius: '10px',
                  color: '#fff',
                }}
              />
              <label
                style={{
                  padding: '12px 16px',
                  background: '#111827',
                  border: '1px solid #1f2937',
                  borderRadius: '10px',
                  color: '#e5e7eb',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Choose files {files.length > 0 ? files.length : ''}
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  style={{ display: 'none' }}
                />
              </label>
              <button
                onClick={handleCreateTicket}
                style={{
                  padding: '12px 20px',
                  borderRadius: '10px',
                  background: 'linear-gradient(90deg,#00f7d2,#5ce9ff)',
                  color: '#000',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Send
              </button>
            </div>
          </div>
        )}

        {selectedTicket && selectedTicket._id && (
          <div>
            <h2 style={{ color: '#5ce9ff', marginBottom: '10px' }}>
              Ticket #{selectedTicket._id?.slice(-6)} — {selectedTicket.subject}
            </h2>

            {/* STATUS BADGE */}
            <div
              style={{
                padding: '6px 12px',
                background: getStatusColor(selectedTicket.status),
                color: '#000',
                width: 'fit-content',
                borderRadius: '10px',
                marginBottom: '20px',
              }}
            >
              {selectedTicket.status}
            </div>

            {/* CLOSE TICKET BUTTON */}
            {selectedTicket.status !== 'close' && selectedTicket.status !== 'closed' && (
              <button
                onClick={() => setShowCloseModal(true)}
                style={{
                  padding: '8px 14px',
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  marginBottom: '20px',
                  cursor: 'pointer',
                }}
              >
                Close Ticket
              </button>
            )}

            {/* CHAT */}
            <h3 style={{ color: '#00f7d2', marginTop: '30px' }}>Chat</h3>
            <div
              style={{
                background: '#0d1220',
                padding: '16px',
                borderRadius: '12px',
                height: '400px',
                overflowY: 'auto',
                border: '1px solid rgba(255,255,255,0.04)',
                marginBottom: '20px',
              }}
            >
              {ticketMessages.length === 0 ? (
                <p style={{ opacity: 0.6, textAlign: 'center' }}>No messages yet</p>
              ) : (
                ticketMessages.map((msg, i) => {
                  const isCurrentUser = msg.user_id === user?.user_id;
                  return (
                    <div
                      key={i}
                      style={{
                        marginBottom: '12px',
                        textAlign: isCurrentUser ? 'right' : 'left',
                      }}
                    >
                      <div
                        style={{
                          display: 'inline-block',
                          padding: '10px 14px',
                          borderRadius: '12px',
                          background: isCurrentUser ? '#00f7d2' : '#1e293b',
                          color: isCurrentUser ? '#000' : '#fff',
                          maxWidth: '70%',
                        }}
                      >
                        <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>
                          {msg.user_detail?.[0]?.name || 'User'}
                        </div>
                        <div>{msg.message}</div>
                        {msg.file && msg.file.length > 0 && (
                          <div style={{ marginTop: '8px' }}>
                            {msg.file.map((file, idx) => (
                              <a
                                key={idx}
                                href={file}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                style={{ color: isCurrentUser ? '#000' : '#00f7d2', marginRight: '8px' }}
                              >
                                📎 File {idx + 1}
                              </a>
                            ))}
                          </div>
                        )}
                        <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '4px' }}>
                          {moment(msg.createdAt).format('MMM DD, YYYY HH:mm')}
                  </div>
                  </div>
                </div>                
                  );
                })
              )}

              {/* CHAT FILE PREVIEW */}
              {chatFiles.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <h4 style={{ color: '#5ce9ff' }}>Files:</h4>
                  {chatFiles.map((f, i) => (
                    <div key={i} style={{ fontSize: '12px', opacity: 0.8 }}>
                      • {f.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* MESSAGE INPUT + FILE UPLOAD */}
            {selectedTicket.status !== 'close' && selectedTicket.status !== 'closed' && (
              <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                <input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write a message..."
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#111827',
                    border: '1px solid #1f2937',
                    borderRadius: '10px',
                    color: '#fff',
                  }}
                />
                <input
                  type="file"
                  multiple
                  onChange={(e) => setChatFiles(Array.from(e.target.files || []))}
                  style={{ color: '#fff', width: '120px' }}
                />
                <button
                  onClick={handleSendMessage}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '10px',
                    background: 'linear-gradient(90deg,#00f7d2,#5ce9ff)',
                    color: '#000',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Send
                </button>
              </div>
            )}

            {selectedTicket.status === 'close' || selectedTicket.status === 'closed' ? (
              <div style={{ marginTop: '20px', padding: '20px', background: '#1a2234', borderRadius: '10px' }}>
                <h5 style={{ color: '#999999' }}>
                  We are writing to inform you that your support ticket has been marked as resolved.
                  If you encounter any further issues related to this matter please create a new ticket.
                </h5>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* CLOSE MODAL */}
      {showCloseModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: '#111827',
              padding: '30px',
              borderRadius: '14px',
              width: '400px',
              textAlign: 'center',
              border: '1px solid #00f7d2',
            }}
          >
            <h3 style={{ color: '#5ce9ff', marginTop: 0 }}>Confirm Close</h3>
            <p style={{ color: '#e5e7eb', marginTop: '15px', marginBottom: '20px' }}>
              Are you sure you want to close this ticket?
            </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setShowCloseModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '10px',
                  background: '#1e293b',
                  color: '#fff',
                  border: '1px solid #293445',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedTicket && selectedTicket.id === 'NEW') {
                    // For NEW ticket, just cancel the draft
                    setSelectedTicket(null);
                    setForm({ subject: '', message: '' });
                    setFiles([]);
                    setShowCloseModal(false);
                  } else {
                    // For existing ticket, close it
                    handleCloseTicket();
                  }
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '10px',
                  background: 'linear-gradient(90deg,#00f7d2,#5ce9ff)',
                  color: '#000',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ATTENTION MODAL */}
      {showAttentionModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001,
          }}
        >
          <div
            style={{
              background: '#111827',
              padding: '30px',
              borderRadius: '14px',
              width: '500px',
              textAlign: 'center',
              border: '1px solid #00f7d2',
            }}
          >
            <h3 style={{ color: '#5ce9ff', marginTop: 0 }}>
              <b>Attention!</b>
            </h3>
            <div style={{ marginTop: '20px' }}>
              <i className="fa fa-info-circle" style={{ fontSize: '30px', color: '#ef4444' }} />
            </div>
            <p style={{ color: '#e5e7eb', marginTop: '15px' }}>
              Our Support will never ask you to share your personal details like password etc. If
              anyone asks you to share any personal details please report us at{' '}
              <b style={{ color: '#5ce9ff' }}>
                <u>support@pharmacrowd.com</u>
              </b>
            </p>
            <button
              onClick={() => setShowAttentionModal(false)}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                borderRadius: '10px',
                background: '#00f7d2',
                color: '#000',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              I Understand
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
